// DTO del resumen ejecutivo del directivo — rediseño 2026-07 (Dashboard).
//
//   - GET /api/directivo/resumen?desde&hasta[&desde_ant&hasta_ant]
//
// Un solo endpoint sirve la fila de KPIs del Dashboard nuevo con desglose POR
// PLANTA: el FE compone "Todas las plantas" sumando filas y resuelve el filtro
// de planta en cliente (sin refetch — decisión del rediseño: filtros
// instantáneos). Los deltas comparan contra el período anterior EQUIVALENTE
// (regla F3: nunca "vs ayer" en rangos largos): el FE manda desde_ant/hasta_ant
// explícitos porque él conoce la semántica del preset (Hoy → mismo día semana
// anterior; Semana → semana anterior; Mes → mes anterior a la misma altura).
//
// $ (valor, costo MO, tiendas, paros): null cuando el user no tiene
// puede_ver_costos — el FE reacomoda el layout (estado transversal del brief).
// Paros en $ = VALOR NO PRODUCIDO (unidades perdidas × precio vigente, fórmula
// C6) — decisión 2026-07-04: NO "MO improductiva".
//
// DN10 (responsable 2026-09-09): el dinero del directivo va sobre producción
// VALIDADA por supervisión — la misma base del informe gerencial del
// coordinador. Lo capturado sin validar se DECLARA aparte como
// `pendiente_validacion_*` (capturado − validado, misma fórmula) y NO suma.
// Las cantidades operativas (unidades, cumplimiento, paros) siguen capturadas.
// `pendiente_historico` es el arrastre total de bloques sin validar de las
// plantas visibles, sin filtro de período.
import { z } from 'zod';
import { pendienteValidacionUtilidadSchema } from './coordinador_cumplimiento.js';
// =============================================================================
// Query
// =============================================================================
export const directivoResumenQuerySchema = z
    .object({
    desde: z.string().date(),
    hasta: z.string().date(),
    desde_ant: z.string().date().optional(),
    hasta_ant: z.string().date().optional(),
})
    .refine((q) => q.desde <= q.hasta, {
    message: 'desde debe ser <= hasta',
})
    .refine((q) => (q.desde_ant === undefined) === (q.hasta_ant === undefined), {
    message: 'desde_ant y hasta_ant van juntos (ambos o ninguno)',
})
    .refine((q) => q.desde_ant === undefined || q.desde_ant <= q.hasta_ant, {
    message: 'desde_ant debe ser <= hasta_ant',
});
// =============================================================================
// Fila por planta (los KPIs se derivan en el FE sumando/filtrando estas filas)
// =============================================================================
export const resumenPlantaPeriodoSchema = z.object({
    planta_id: z.string().uuid(),
    nombre: z.string(),
    codigo: z.string(),
    // Volumen y metas (unidades) — fuente vw_kpis_linea_turno (MVs v3)
    unidades: z.number(),
    meta_capacidad_unds: z.number(),
    // Cumplimiento vs programa cliente — reconciliación por área (WS-4)
    programa_unds: z.number(),
    real_planif_unds: z.number(),
    fuera_plan_unds: z.number(),
    // Paros (solo causales que afectan disponibilidad, fórmula C6). Solo paros
    // CERRADOS suman minutos/eventos (DN1); los abiertos van en paros_en_curso.
    paros_min: z.number(),
    paros_eventos: z.number(),
    /** DN1: paros ABIERTOS del período en la planta — aparte, no suman. */
    paros_en_curso: z.number().int().min(0),
    paros_unds_perdidas: z.number(),
    // ── La plata (null si !puede_ver_costos) ──────────────────────────────────
    valor_lineas_cop: z.number().nullable(),
    costo_mo_lineas_cop: z.number().nullable(),
    valor_tiendas_cop: z.number().nullable(),
    ingreso_insumos_tiendas_cop: z.number().nullable(),
    costo_mo_tiendas_cop: z.number().nullable(),
    paros_valor_cop: z.number().nullable(),
    // DN9: producción sin precio vigente, fuera del cálculo de utilidad y declarada.
    sin_tarifa_lineas_paq: z.number().nullable().optional(),
    sin_tarifa_lineas_skus: z.number().int().nullable().optional(),
    sin_tarifa_lineas_costo_mo_cop: z.number().nullable().optional(),
    sin_tarifa_tiendas_paq: z.number().nullable().optional(),
    sin_tarifa_tiendas_skus: z.number().int().nullable().optional(),
    sin_tarifa_tiendas_costo_mo_cop: z.number().nullable().optional(),
    /** Bloques con precio pero sin costo hora-persona vigente: fuera de la utilidad, declarados. */
    sin_costo_mo_lineas_paq: z.number().nullable().optional(),
    sin_costo_mo_lineas_valor_cop: z.number().nullable().optional(),
    sin_costo_mo_tiendas_paq: z.number().nullable().optional(),
    sin_costo_mo_tiendas_valor_cop: z.number().nullable().optional(),
    /** DN10: la plata de esta fila (líneas y tiendas) es sobre producción VALIDADA. */
    base_utilidad: z.literal('validada').optional(),
    /** DN10: capturado − validado de líneas — declarado, NO sumado. null sin puede_ver_costos. */
    pendiente_validacion_lineas: pendienteValidacionUtilidadSchema.nullable().optional(),
    /** DN10: capturado − validado de tiendas (valor incluye insumos cobrados). null sin costos. */
    pendiente_validacion_tiendas: pendienteValidacionUtilidadSchema.nullable().optional(),
});
// =============================================================================
// Arrastre histórico de bloques sin validar (DN10) — sin filtro de período
// =============================================================================
export const pendienteHistoricoPlantaSchema = z.object({
    planta_id: z.string().uuid(),
    bloques: z.number().int().min(0),
    paquetes: z.number().min(0),
    /** Fecha del bloque pendiente más antiguo (null si no hay pendientes). */
    desde: z.string().date().nullable(),
});
export const pendienteHistoricoSchema = z.object({
    bloques: z.number().int().min(0),
    paquetes: z.number().min(0),
    desde: z.string().date().nullable(),
    /** Desglose por planta visible (el FE filtra planta en cliente). */
    por_planta: z.array(pendienteHistoricoPlantaSchema),
});
// =============================================================================
// Estado operativo por planta (solo del período ACTUAL — no comparable)
// =============================================================================
export const areaEstadoSchema = z.object({
    area_id: z.string().uuid(),
    nombre: z.string(),
    unidades: z.number(),
    meta_capacidad_unds: z.number(),
    /** Alguna línea del área con paro abierto AHORA (solo relevante en Hoy). */
    paro_activo: z.boolean(),
});
export const estadoPlantaResumenSchema = z.object({
    planta_id: z.string().uuid(),
    /** Turno corriendo AHORA en la planta (null fuera de turno). */
    turno_activo_numero: z.number().int().nullable(),
    /** HH:MM de la incidencia P1_sin_captura pendiente más antigua (null si no hay). */
    sin_captura_desde_hhmm: z.string().nullable(),
    /** Tira de áreas para los chips de la card (sobre el rango actual). */
    areas: z.array(areaEstadoSchema),
    /** Confiabilidad del dato: capturas válidas y cuántas validó el supervisor. */
    capturas_total: z.number(),
    capturas_validadas: z.number(),
    /** SKUs de tienda del rango sin precio vigente (aviso "valor parcial"). */
    tiendas_skus_sin_precio: z.number(),
});
// =============================================================================
// Respuesta
// =============================================================================
export const directivoResumenSchema = z.object({
    rango: z.object({ desde: z.string().date(), hasta: z.string().date() }),
    rango_anterior: z.object({ desde: z.string().date(), hasta: z.string().date() }).nullable(),
    puede_ver_costos: z.boolean(),
    /** Una fila por planta activa del tenant (aunque no tenga datos en el rango). */
    plantas: z.array(resumenPlantaPeriodoSchema),
    /** Mismas plantas sobre el rango anterior (null si no se pidió). */
    plantas_anterior: z.array(resumenPlantaPeriodoSchema).nullable(),
    /** Estado operativo (badges/chips/confiabilidad) — solo del rango actual. */
    estado_plantas: z.array(estadoPlantaResumenSchema),
    /** Día operativo de HOY (madrugada de turno que cruza → ayer). */
    fecha_operativa_hoy: z.string().date(),
    /** HH:MM de la última captura del rango (corte del dato; null sin capturas). */
    corte_hhmm: z.string().nullable(),
    /** DN10: base del dinero de todas las filas. */
    base_utilidad: z.literal('validada').optional(),
    /**
     * DN10: pendiente de validar del rango, CONSOLIDADO líneas + tiendas de todas
     * las plantas visibles (suma de las filas de `plantas`). null sin costos.
     */
    pendiente_validacion_utilidad: pendienteValidacionUtilidadSchema.nullable().optional(),
    /** DN10: arrastre histórico de bloques sin validar (todas las fechas). */
    pendiente_historico: pendienteHistoricoSchema.optional(),
    actualizado_at: z.string(),
});
//# sourceMappingURL=directivo_resumen.js.map